import { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import { notifyContentUpdated } from "../../lib/contentSync";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { PencilSimple, Trash, Plus, CaretUp, CaretDown, MagnifyingGlass } from "@phosphor-icons/react";
import ImageEditorModal from "./ImageEditorModal";

export default function ResourceTab({ title, endpoint, fetchKey, fields, columns, testid, reorder = false, statusToggle = false, groupBy = "", searchKey = "", searchPlaceholder = "" }) {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");

    const [athleteOptions, setAthleteOptions] = useState([]);
    const visibleItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q || !searchKey) return items;
        return items.filter((item) => String(item[searchKey] || "").toLowerCase().includes(q));
    }, [items, search, searchKey]);
    const groupedItems = useMemo(() => {
        if (!groupBy) return [];
        const map = {};
        visibleItems.forEach((item) => {
            const name = String(item[groupBy] || "").trim() || "Tanpa Event";
            if (!map[name]) map[name] = { name, items: [] };
            map[name].items.push(item);
        });
        return Object.values(map).sort((a, b) => {
            const latestA = Math.max(...a.items.map((item) => Number(item.year) || 0));
            const latestB = Math.max(...b.items.map((item) => Number(item.year) || 0));
            return latestB - latestA || a.name.localeCompare(b.name);
        });
    }, [visibleItems, groupBy]);
    const load = () =>
        api.get("/content")
            .then((r) => {
                setItems(r.data[fetchKey] || []);
                setAthleteOptions((r.data.athletes || []).map((a) => a.name));
            })
            .catch(() => toast.error("Gagal memuat data"));

    useEffect(() => {
        load();
    }, []);

    const defaults = () => Object.fromEntries(fields.map((f) => [f.name, f.default ?? ""]));
    const openCreate = () => {
        setEditing(null);
        setForm(defaults());
        setOpen(true);
    };
    const openEdit = (item) => {
        setEditing(item);
        setForm(Object.fromEntries(fields.map((f) => [f.name, item[f.name] ?? f.default ?? ""])));
        setOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editing) await api.put(`/admin/${endpoint}/${editing.id}`, form);
            else await api.post(`/admin/${endpoint}`, form);
            toast.success("Data tersimpan");
            notifyContentUpdated();
            setOpen(false);
            load();
        } catch {
            toast.error("Gagal menyimpan data");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (item) => {
        if (!window.confirm(`Hapus "${item[columns[0].key]}"?`)) return;
        try {
            await api.delete(`/admin/${endpoint}/${item.id}`);
            toast.success("Data dihapus");
            notifyContentUpdated();
            load();
        } catch {
            toast.error("Gagal menghapus");
        }
    };

    const move = async (item, dir) => {
        try {
            await api.post(`/admin/${endpoint}/${item.id}/move`, { direction: dir });
            notifyContentUpdated();
            load();
        } catch {
            toast.error("Gagal mengubah urutan");
        }
    };

    const toggleStatus = async (item) => {
        const next = (item.status ?? "active") === "inactive" ? "active" : "inactive";
        if (next === "inactive" && !window.confirm(`Nonaktifkan "${item[columns[1]?.key] || item[columns[0].key]}"? Data historis (iuran, absensi, rapot) tetap tersimpan.`)) return;
        try {
            await api.patch(`/admin/${endpoint}/${item.id}/status`, { status: next });
            toast.success(next === "inactive" ? "Atlet dinonaktifkan" : "Atlet diaktifkan kembali");
            notifyContentUpdated();
            load();
        } catch {
            toast.error("Gagal mengubah status atlet");
        }
    };

    const [uploading, setUploading] = useState("");
    const [editor, setEditor] = useState(null);

    const uploadImageBlob = async (fieldName, blob) => {
        setUploading(fieldName);
        try {
            const fd = new FormData();
            fd.append("file", blob, "foto.jpg");
            const r = await api.post("/admin/upload", fd);
            setForm((prev) => ({ ...prev, [fieldName]: r.data.url }));
            toast.success("Gambar terunggah");
        } catch {
            toast.error("Gagal mengunggah gambar");
        } finally {
            setUploading("");
        }
    };

    const pickImageFile = (fieldName, file, aspect) => {
        if (!file) return;
        setEditor({ src: URL.createObjectURL(file), fieldName, aspect });
    };

    const editExistingImage = async (fieldName, aspect) => {
        try {
            const res = await fetch(fileUrl(form[fieldName]));
            const blob = await res.blob();
            setEditor({ src: URL.createObjectURL(blob), fieldName, aspect });
        } catch {
            toast.error("Gagal memuat gambar untuk diedit");
        }
    };

    const closeEditor = () => {
        if (editor?.src) URL.revokeObjectURL(editor.src);
        setEditor(null);
    };

    const onEditorSave = async (blob) => {
        const fieldName = editor.fieldName;
        closeEditor();
        await uploadImageBlob(fieldName, blob);
    };

    const renderRow = (item) => {
        const i = items.indexOf(item);
        return (
            <tr key={item.id} className="border-b border-racing-line last:border-0 hover:bg-black/[0.03]" data-testid={`${testid}-row-${i}`}>
                {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-neutral-800 max-w-[240px] truncate">
                        {c.render ? c.render(item) : item[c.key]}
                    </td>
                ))}
                <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                        {statusToggle && (
                            <button
                                onClick={() => toggleStatus(item)}
                                data-testid={`${testid}-status-${i}`}
                                className={`px-3 py-2 border text-[10px] uppercase tracking-[0.15em] transition-colors ${(item.status ?? "active") === "inactive" ? "border-racing-gold text-racing-golddeep hover:bg-racing-gold/10" : "border-racing-line text-racing-muted hover:text-racing-red hover:border-racing-red"}`}
                            >
                                {(item.status ?? "active") === "inactive" ? "Aktifkan" : "Nonaktifkan"}
                            </button>
                        )}
                        {reorder && (
                            <>
                                <button
                                    onClick={() => move(item, "up")}
                                    data-testid={`${testid}-up-${i}`}
                                    className="p-2 border border-racing-line text-racing-muted hover:text-neutral-900 hover:border-black/40 transition-colors"
                                    aria-label="Geser naik"
                                >
                                    <CaretUp size={16} />
                                </button>
                                <button
                                    onClick={() => move(item, "down")}
                                    data-testid={`${testid}-down-${i}`}
                                    className="p-2 border border-racing-line text-racing-muted hover:text-neutral-900 hover:border-black/40 transition-colors"
                                    aria-label="Geser turun"
                                >
                                    <CaretDown size={16} />
                                </button>
                            </>
                        )}
                        <button
                            onClick={() => openEdit(item)}
                            data-testid={`${testid}-edit-${i}`}
                            className="p-2 border border-racing-line text-racing-muted hover:text-white hover:border-white/40 transition-colors"
                            aria-label="Edit"
                        >
                            <PencilSimple size={16} />
                        </button>
                        <button
                            onClick={() => remove(item)}
                            data-testid={`${testid}-delete-${i}`}
                            className="p-2 border border-racing-line text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors"
                            aria-label="Hapus"
                        >
                            <Trash size={16} />
                        </button>
                    </div>
                </td>
            </tr>
        );
    };

    const renderTable = (rows) => (
        <div className="border border-racing-line overflow-x-auto bg-white">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-racing-line bg-racing-surface">
                        {columns.map((c) => (
                            <th key={c.key} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-racing-muted font-medium">
                                {c.label}
                            </th>
                        ))}
                        <th className="px-4 py-3" />
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-racing-muted" data-testid={`${testid}-empty`}>
                                Belum ada data.
                            </td>
                        </tr>
                    )}
                    {rows.map((item) => renderRow(item))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div data-testid={`${testid}-tab`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <h2 className="font-display uppercase text-2xl text-neutral-900">{title}</h2>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    {searchKey && (
                        <div className="relative">
                            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-racing-muted" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={searchPlaceholder || "Ketik sebagian nama..."}
                                data-testid={`${testid}-search-input`}
                                className="bg-white border border-racing-line pl-10 pr-4 py-3 text-sm w-full md:w-72 focus:outline-none focus:border-racing-red transition-colors"
                            />
                        </div>
                    )}
                    <button
                        onClick={openCreate}
                        data-testid={`${testid}-add-button`}
                        className="flex items-center justify-center gap-2 bg-racing-red hover:bg-racing-redhover text-white text-xs uppercase tracking-[0.2em] px-5 py-3 transition-colors duration-300"
                    >
                        <Plus size={16} weight="bold" /> Tambah
                    </button>
                </div>
            </div>

            {searchKey && search && (
                <p className="text-xs text-racing-muted mb-4" data-testid={`${testid}-search-count`}>{visibleItems.length} data cocok dengan "{search}"</p>
            )}

            {groupBy ? (
                <div className="space-y-7" data-testid={`${testid}-grouped-list`}>
                    {groupedItems.length === 0 ? renderTable([]) : groupedItems.map((group, groupIndex) => (
                        <section key={group.name} data-testid={`${testid}-event-group-${groupIndex}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2 bg-racing-red px-4 py-3 border border-racing-red">
                                <h3 className="font-display uppercase text-lg text-white" data-testid={`${testid}-event-title-${groupIndex}`}>{group.name}</h3>
                                <span className="text-[10px] uppercase tracking-[0.2em] text-white/75" data-testid={`${testid}-event-count-${groupIndex}`}>{group.items.length} data</span>
                            </div>
                            {renderTable(group.items)}
                        </section>
                    ))}
                </div>
            ) : renderTable(visibleItems)}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase tracking-wide text-xl">
                            {editing ? "Edit" : "Tambah"} {title}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4 mt-2" data-testid={`${testid}-form`}>
                        {fields.map((f) => (
                            <div key={f.name}>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">{f.label}</label>
                                {f.type === "athlete" ? (
                                    athleteOptions.length > 0 ? (
                                        <select
                                            required
                                            value={form[f.name] ?? ""}
                                            onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                                            data-testid={`${testid}-field-${f.name}`}
                                            className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                        >
                                            <option value="" disabled>Pilih atlet (kelola di tab Atlet)</option>
                                            {athleteOptions.map((n) => (
                                                <option key={n} value={n}>{n}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            required
                                            value={form[f.name] ?? ""}
                                            onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                                            placeholder="Tambahkan atlet dulu di tab Atlet"
                                            data-testid={`${testid}-field-${f.name}`}
                                            className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                        />
                                    )
                                ) : f.type === "textarea" ? (
                                    <textarea
                                        required={f.required !== false}
                                        rows={4}
                                        value={form[f.name] ?? ""}
                                        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                                        data-testid={`${testid}-field-${f.name}`}
                                        className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                    />
                                ) : f.type === "image" ? (
                                    <div>
                                        {form[f.name] ? (
                                            <div className="mb-2 flex items-end gap-3">
                                                <img src={fileUrl(form[f.name])} alt="" className="h-24 w-32 object-cover border border-racing-line" />
                                                <button
                                                    type="button"
                                                    onClick={() => editExistingImage(f.name, f.aspect)}
                                                    data-testid={`${testid}-edit-${f.name}`}
                                                    className="border border-racing-line px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors"
                                                >
                                                    Edit Foto
                                                </button>
                                            </div>
                                        ) : null}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; pickImageFile(f.name, file, f.aspect); }}
                                            data-testid={`${testid}-file-${f.name}`}
                                            className="w-full text-xs text-racing-muted file:mr-3 file:bg-racing-surface file:border file:border-racing-line file:px-3 file:py-2 file:text-xs file:uppercase file:tracking-[0.2em] file:text-white file:cursor-pointer"
                                        />
                                        {uploading === f.name && <p className="mt-1 text-xs text-racing-red">Mengunggah...</p>}
                                        <input
                                            type="text"
                                            value={form[f.name] ?? ""}
                                            onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                                            placeholder="atau tempel URL gambar"
                                            data-testid={`${testid}-field-${f.name}`}
                                            className="mt-2 w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                        />
                                    </div>
                                ) : f.type === "select" ? (
                                    <select
                                        value={form[f.name] ?? ""}
                                        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                                        data-testid={`${testid}-field-${f.name}`}
                                        className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                    >
                                        {f.options.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <>
                                        <input
                                            type={f.type === "number" ? "number" : "text"}
                                            required={f.required !== false}
                                            value={form[f.name] ?? ""}
                                            onChange={(e) =>
                                                setForm({ ...form, [f.name]: f.type === "number" ? Number(e.target.value) : e.target.value })
                                            }
                                            list={f.suggest ? `${testid}-suggest-${f.name}` : undefined}
                                            data-testid={`${testid}-field-${f.name}`}
                                            className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                        />
                                        {f.suggest ? (
                                            <datalist id={`${testid}-suggest-${f.name}`} data-testid={`${testid}-suggest-${f.name}`}>
                                                {[...new Set(items.map((it) => it[f.name]).filter(Boolean))].map((v) => (
                                                    <option key={v} value={v} />
                                                ))}
                                            </datalist>
                                        ) : null}
                                    </>
                                )}
                            </div>
                        ))}
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid={`${testid}-save-button`}
                            className="w-full bg-racing-red hover:bg-racing-redhover disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-3.5 transition-colors duration-300"
                        >
                            {saving ? "Menyimpan..." : "Simpan"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>

            <ImageEditorModal
                open={!!editor}
                imageSrc={editor?.src}
                aspect={editor?.aspect || 4 / 3}
                onCancel={closeEditor}
                onSave={onEditorSave}
            />
        </div>
    );
}
